import {
  Box,
  FlexBox,
  IconButton,
  ModalFoot,
  ModalHead,
  Skeleton,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import {
  createElement,
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useSchedules } from 'components/settings/components/sections/openingHours/utils/useSchedules';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';

import { AudioMessage } from './components/audioMessage/audioMessage';
import { ExistingSchedule } from './components/existingSchedule/existingSchedule';
import { Holidays } from './components/holidays/holidays';
import { NameSchedule } from './components/nameSchedule/nameSchedule';
import { ReviewHours } from './components/reviewHours/reviewHours';
import { MigrateOpeningHoursContext } from './migrateOpeningHours.context';
import type { BusinessHourSchedule } from './types/businessHourSchedule';
import type { HolidayState } from './types/holidays';
import type { BaseProps as Props } from './types/props';

type MigrationStep = {
  title: string;
  view: FunctionComponent<Props>;
};

const steps: MigrationStep[] = [
  {
    title: 'Review hours',
    view: ReviewHours,
  },
  {
    title: 'Connect holiday calendar (optional)',
    view: Holidays,
  },
  {
    title: 'Set audio message',
    view: AudioMessage,
  },
  {
    title: 'Name your schedule',
    view: NameSchedule,
  },
];

export function MigrateOpeningHours(props: Props): JSX.Element {
  const { node, triggerClose } = props;
  const { schedule: openingHourSchedule = [], timezone } = node.payload || {};

  const initialSchedule = useMemo(
    () =>
      openingHourSchedule.map(({ date, intervals }) => ({
        date,
        intervals: intervals.map(({ open, close }) => ({
          start: moment.utc(open).format('HH:mm'),
          end: moment.utc(close).format('HH:mm'),
        })),
      })),
    [openingHourSchedule],
  );
  const [schedule, setSchedule] =
    useState<BusinessHourSchedule[]>(initialSchedule);
  const [holidays, setHolidays] = useState<HolidayState>(null);
  const [announcement, setAnnouncement] = useState<AnnouncementItem | null>(
    null,
  );
  const [name, setName] = useState('');
  const [intervalsErrors, setIntervalsErrors] = useState<(null | string)[][]>(
    [],
  );

  const [step, setStep] = useState(0);
  const { title, view } = steps[step];
  const handlePrevStep = () => setStep((step) => step - 1);

  const [isIgnoringExistingSchedules, setIsIgnoringExistingSchedules] =
    useState(false);
  const { currentPageSchedules, asyncStatus } = useSchedules();
  const isScheduleListLoading = asyncStatus === 'pending';
  const hasSchedules = currentPageSchedules.length > 0;

  const handleIgnoreExistingSchedules = () =>
    setIsIgnoringExistingSchedules(true);

  const handleCancel = useCallback(() => {
    ampli.businessHoursMigrationWizardFormCancelled({ step: step + 1 });
    triggerClose();
  }, [step, triggerClose]);

  const handleNextStep = useCallback(() => {
    // The payload sent to the analytics is determined by the
    // step the modal was confirmed with
    ampli.businessHoursMigrationWizardFormSubmitted({
      step: step + 1,
      ...(step === 0 && {
        hasIntervalsChanged: !isEqual(initialSchedule, schedule),
      }),
      ...(step === 1 &&
        holidays && {
          holidays: holidays.countryCode,
          isClosed: holidays.isClosedOnHolidays,
        }),
    });

    setStep(step + 1);
  }, [holidays, initialSchedule, schedule, step]);

  const context = useMemo(
    () => ({
      schedule,
      holidays,
      intervalsErrors,
      announcement,
      name,
      timezone: timezone || moment.tz.guess(),
      setters: {
        setIntervalsErrors,
        setSchedule,
        setHolidays,
        setAnnouncement,
        setName,
        setStep,
      },
      step,
      onCancel: handleCancel,
      onNextStep: handleNextStep,
    }),
    [
      announcement,
      holidays,
      intervalsErrors,
      name,
      schedule,
      step,
      timezone,
      handleCancel,
      handleNextStep,
    ],
  );

  return (
    <MigrateOpeningHoursContext.Provider value={context}>
      <Box width="600px">
        {hasSchedules && !isIgnoringExistingSchedules ? (
          <ExistingSchedule
            {...props}
            onCreateNewSchedule={handleIgnoreExistingSchedules}
          />
        ) : (
          <>
            <ModalHead
              title={
                <FlexBox gap="200" alignItems="center">
                  {step > 0 && (
                    <IconButton
                      variant="tertiary"
                      icon="angle-left"
                      iconSize={24}
                      onClick={handlePrevStep}
                    />
                  )}
                  {isScheduleListLoading ? (
                    <Skeleton width="150px" height="16px" />
                  ) : (
                    <>
                      {title}
                      <Text.Paragraph subdued variant="2">
                        Step {step + 1} of {steps.length}
                      </Text.Paragraph>
                    </>
                  )}
                </FlexBox>
              }
              onClose={handleCancel}
            />
            {isScheduleListLoading ? (
              <>
                <FlexBox justifyContent="center" margin="280px 0">
                  <Spinner />
                </FlexBox>
                <ModalFoot cancelOnClick={handleCancel} />
              </>
            ) : (
              createElement(view, props)
            )}
          </>
        )}
      </Box>
    </MigrateOpeningHoursContext.Provider>
  );
}
